import * as RadixSelect from '@radix-ui/react-select';
import Icon from 'components/Icon';
import icons from 'components/Icon/iconPath';
import React from 'react';
import { TSelectOption } from 'types';

type SelectClasses = {
  trigger?: string;
  content?: string;
  viewport?: string;
  item?: string;
  value?: string;
};

export type CustomSelectProps = {
  name?: string;
  value: string;
  options: TSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: React.ReactNode;
  ariaLabel?: string;
  icon?: keyof typeof icons;
  renderValue?: (option?: TSelectOption) => React.ReactNode;
  renderOption?: (option: TSelectOption) => React.ReactNode;
  classes?: SelectClasses;
  position?: 'item-aligned' | 'popper';
  sideOffset?: number;
  align?: 'start' | 'end';
  indicator?: React.ReactElement;
};

const DEFAULT_TRIGGER_CLASS =
  'relative flex w-full cursor-pointer items-center gap-2 rounded-lg bg-white py-2 pl-3 pr-8 text-left font-medium ring-1 ring-neutral-300 focus:outline-none data-[disabled]:pointer-events-none data-[disabled]:bg-neutral-50 sm:leading-6';
const DEFAULT_CONTENT_CLASS =
  'z-50 max-h-[min(14rem,var(--radix-select-content-available-height))] w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-xl bg-white text-xs shadow-lg ring-1 ring-black ring-opacity-5';
const DEFAULT_VIEWPORT_CLASS = 'divide-y divide-neutral-100';
const DEFAULT_ITEM_CLASS =
  'relative cursor-pointer select-none py-3 text-center font-bold text-neutral-900 outline-none data-[disabled]:pointer-events-none data-[disabled]:bg-neutral-100 data-[disabled]:text-neutral-400 data-[highlighted]:bg-neutral-25 data-[state=checked]:bg-[#F0FAF0] data-[state=checked]:text-primary';

function CustomSelect({
  name,
  value,
  options,
  onChange,
  disabled = false,
  placeholder,
  ariaLabel,
  icon,
  renderValue,
  renderOption,
  classes,
  position = 'popper',
  sideOffset = 4,
  align = 'start',
  indicator,
}: CustomSelectProps) {
  const selectedOption = options.find((option) => option.value === value);
  const valueContent = renderValue
    ? renderValue(selectedOption)
    : selectedOption?.name;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  // 一覧を開いたままボタンをタッチすると、押し始めで一覧が閉じ、離したあとの click が
  // ボタンに届いて Radix が開き直す。ボタンの上で閉じたときは、その click を 1 回だけ無視する
  const ignoreNextClickRef = React.useRef(false);

  const handlePointerDownOutside = (
    event: CustomEvent<{ originalEvent: PointerEvent }>,
  ) => {
    const rect = triggerRef.current?.getBoundingClientRect();
    const { clientX, clientY } = event.detail.originalEvent;
    ignoreNextClickRef.current =
      !!rect &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!ignoreNextClickRef.current) return;
    ignoreNextClickRef.current = false;
    // Radix の開く処理を止める
    event.preventDefault();
  };

  return (
    <RadixSelect.Root
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      name={name}
    >
      <RadixSelect.Trigger
        ref={triggerRef}
        className={classes?.trigger ?? DEFAULT_TRIGGER_CLASS}
        aria-label={ariaLabel}
        onPointerDown={() => {
          // ボタンの上で押し始めた操作は、閉じた一覧の続きではない
          ignoreNextClickRef.current = false;
        }}
        onClick={handleTriggerClick}
      >
        {icon ? (
          <span className="flex flex-shrink-0 items-center">
            <Icon
              icon={icon}
              width={20}
              height={20}
              color="var(--color-neutral-400)"
            />
          </span>
        ) : null}
        <RadixSelect.Value asChild>
          <span className={classes?.value ?? 'block min-w-0 flex-1 truncate'}>
            {valueContent ?? placeholder}
          </span>
        </RadixSelect.Value>
        <RadixSelect.Icon asChild>
          {indicator ?? (
            <span
              className="pointer-events-none absolute inset-y-0 right-3 flex items-center"
              aria-hidden
            >
              <span className="inline-block border-x-[5px] border-t-[6px] border-x-transparent border-t-neutral" />
            </span>
          )}
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position={position}
          side="bottom"
          align={align}
          avoidCollisions={false}
          sideOffset={position === 'popper' ? sideOffset : undefined}
          className={classes?.content ?? DEFAULT_CONTENT_CLASS}
          onPointerDownOutside={handlePointerDownOutside}
        >
          <RadixSelect.Viewport
            className={classes?.viewport ?? DEFAULT_VIEWPORT_CLASS}
          >
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                textValue={option.name}
                className={classes?.item ?? DEFAULT_ITEM_CLASS}
              >
                <RadixSelect.ItemText asChild>
                  <span>
                    {renderOption ? renderOption(option) : option.name}
                  </span>
                </RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

export default CustomSelect;
